import { useTranslation } from 'react-i18next';

import { Combobox, type ComboboxOption } from './Combobox';

interface MultiComboboxProps {
  id?: string;
  ariaLabel?: string;
  options: ComboboxOption[];
  /** Die Kennungen der gewaehlten Eintraege, in der Reihenfolge der Wahl. */
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Mehrere Eintraege waehlen -- als Marken ueber einem Auswahlfeld.
 *
 * **Ersetzt ``<select multiple>`` und lange Listen mit Kaestchen.** Die
 * Mehrfachauswahl verlor bei einem Klick ohne Steuerungstaste lautlos alles
 * Gewaehlte, und eine Liste mit dreissig Kaestchen muss man durchlesen, um
 * einen Namen zu finden. Hier steht oben, was gewaehlt ist; darunter tippt
 * man und waehlt den naechsten. Ein Klick auf eine Marke nimmt ihren Eintrag
 * wieder heraus -- genau diesen, keinen anderen.
 *
 * Bereits Gewaehltes erscheint in der Liste nicht noch einmal.
 */
export function MultiCombobox({
  id,
  ariaLabel,
  options,
  value,
  onChange,
  placeholder,
  disabled,
}: MultiComboboxProps) {
  const { t } = useTranslation();
  const labelOf = (key: string) => options.find((option) => option.id === key)?.label ?? key;

  return (
    <div className="multi-combobox">
      {value.length > 0 ? (
        <div className="kb-chips">
          {value.map((key) => (
            <button
              key={key}
              type="button"
              className="kb-chip"
              disabled={disabled}
              aria-label={t('combobox.remove', { name: labelOf(key) })}
              onClick={() => {
                onChange(value.filter((entry) => entry !== key));
              }}
            >
              {labelOf(key)}
              <span className="kb-chip__remove" aria-hidden="true">
                ×
              </span>
            </button>
          ))}
        </div>
      ) : null}
      <Combobox
        id={id}
        ariaLabel={ariaLabel}
        value={null}
        options={options.filter((option) => !value.includes(option.id))}
        placeholder={placeholder ?? t('combobox.add')}
        disabled={disabled}
        onChange={(option) => {
          if (option) onChange([...value, option.id]);
        }}
      />
    </div>
  );
}
